import '../assets/Terms.css';
import { h, defineComponent, computed, ref, openBlock, createElementBlock, Fragment, renderList, createVNode, toDisplayString, createElementVNode, unref, createCommentVNode } from 'vue';
import { T as TextTransform, c as TermsLinkKind } from './maps.nxkzRTum.js';
import { s as setTermsText, t as toggleModal } from './useModal.CtQMkYkB.js';
import { o as useStore, m as toTitleCase } from './useStore.DAPvMbcE.js';
import { _ as _sfc_main$1 } from './Checkbox.vue_vue_type_style_index_0_lang.BqtXIdYk.js';

const icon = (props, context) => {
    const xmlns = "http://www.w3.org/2000/svg";
    const svgCode = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
  <path fill-rule="evenodd" d="M5.917 5.833a.75.75 0 01.75-.75h7.5a.75.75 0 01.75.75v7.5a.75.75 0 01-1.5 0V7.644l-7.053 7.053a.75.75 0 11-1.061-1.06l7.053-7.054h-5.69a.75.75 0 01-.75-.75z" clip-rule="evenodd"/>
</svg>
`;
    
    const svgRoot = new DOMParser().parseFromString(svgCode, 'image/svg+xml').firstChild;
    
    if (props.title) {
      const title = document.createElementNS(xmlns, 'title');
      title.textContent = props.title;
      svgRoot.appendChild(title);
    }

    const svgAttributes = Array
      .from(svgRoot?.attributes ?? [])
      .reduce((acc, curr) => ({
        ...acc,
        [curr.name]: curr.value
      }));

    return h(
      'svg',
      {
        ...svgAttributes,
        ...context.attrs,
        role: 'img',
        innerHTML: svgRoot.innerHTML
      },
    );
  };

const _hoisted_1 = {
  class: "thn--tool-terms",
  role: "dialog",
  "aria-modal": "true",
  "aria-labelledby": "thn--tool-terms__label"
};
const _hoisted_2 = { key: 0 };
const _hoisted_3 = ["for"];
const _hoisted_4 = ["href", "aria-label"];
const _hoisted_5 = ["onClick", "aria-label"];
const _hoisted_6 = ["aria-label"];
const _hoisted_7 = {
  key: 4,
  class: "thn--tool-terms__label thn--tool-terms__label--tag"
};
const _hoisted_8 = ["for"];
const _hoisted_9 = ["innerHTML", "for"];
const _hoisted_10 = ["href", "target", "aria-label"];
const _hoisted_11 = ["onClick", "aria-label"];
const _hoisted_12 = ["aria-label"];
const _hoisted_13 = {
  key: 5,
  class: "thn--tool-terms__label thn--tool-terms__label--tag"
};
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "Terms",
  props: {
    customTerms: {},
    propertyTerms: {},
    termsInherited: { type: Boolean },
    termsRequired: { type: Boolean },
    disabled: { type: Boolean },
    appearanceStyle: {}
  },
  setup(__props, { expose: __expose }) {
    const store = useStore();
    const props = __props;
    const filteredCustomTerms = computed(() => {
      const propertyTermIds = (props.propertyTerms ?? []).map((term) => term.id);
      return (props.customTerms ?? []).filter((customTerm) => !props.termsInherited || !propertyTermIds.includes(customTerm.termId));
    });
    const termRefs = ref([]);
    const isTitleCase = computed(
      () => store?.styles.fonts.formFieldName?.textTransform === TextTransform.Titlecase
    );
    const errorMessage = computed(() => store?.literals.getLiteral("Please agree to the Terms and Conditions"));
    const optionalLabel = store?.literals.getLiteral("(Optional)");
    const defaultTermsLabel = computed(() => store?.literals.getLiteral("Terms_default_label", isTitleCase.value));
    const defaultTerms = computed(() => store?.literals.getLiteral("Terms_default"));
    function validate() {
      const response = {
        valid: termRefs.value.every((termRef) => termRef?.validate()),
        terms: []
      };
      if (response.valid) {
        response.terms = termRefs.value.map((termRef) => ({
          termId: termRef.id,
          isChecked: termRef.isChecked
        }));
      }
      return response;
    }
    function getTermLabel(moduleTerms) {
      if (!moduleTerms.content.languages) return "";
      return isTitleCase.value ? toTitleCase(moduleTerms.content.languages[store?.literals.language || "en"]?.label || "") : moduleTerms.content.languages[store?.literals.language || "en"]?.label;
    }
    function getTermText(moduleTerms) {
      return moduleTerms.content.languages?.[store?.literals.language || "en"]?.text;
    }
    function getTermLink(moduleTerms) {
      return moduleTerms.content.languages?.[store?.literals.language || "en"]?.url;
    }
    function isNotLinkedTerm(moduleTerms) {
      return moduleTerms.content.linkedTo === TermsLinkKind.NotLinked;
    }
    function isUrlTerm(moduleTerms) {
      return moduleTerms.content.linkedTo === TermsLinkKind.Url;
    }
    function isCustomUrlTerm(moduleTerms) {
      return moduleTerms.content.linkedTo === TermsLinkKind.CustomUrl;
    }
    function isPopupTerm(moduleTerms) {
      return [TermsLinkKind.CustomText].includes(moduleTerms.content.linkedTo);
    }
    function isDefaultPopupTerm(moduleTerms) {
      return [TermsLinkKind.DefaultText].includes(moduleTerms.content.linkedTo);
    }
    function openTerms(moduleTerms) {
      setTermsText(getTermText(moduleTerms) || "");
      toggleModal();
    }
    function openDefaultTerms() {
      setTermsText(defaultTerms.value || "");
      toggleModal();
    }
    __expose({
      validate
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", _hoisted_1, [
        _ctx.termsInherited && _ctx.propertyTerms?.length ? (openBlock(), createElementBlock("div", _hoisted_2, [
          (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.propertyTerms, (propertyTerm, index) => {
            return openBlock(), createElementBlock("div", {
              class: "thn--tool-terms__item",
              key: index
            }, [
              createVNode(_sfc_main$1, {
                ref_for: true,
                ref: (ref2) => termRefs.value[index] = ref2,
                "error-message": errorMessage.value,
                class: "thn--tool-terms__checkbox",
                disabled: !!_ctx.disabled,
                required: _ctx.termsRequired,
                name: "terms-inherit",
                id: propertyTerm.id
              }, null, 8, ["error-message", "disabled", "required", "id"]),
              isNotLinkedTerm(propertyTerm) ? (openBlock(), createElementBlock("label", {
                key: 0,
                class: "thn--tool-terms__label",
                for: propertyTerm.id
              }, toDisplayString(getTermLabel(propertyTerm)), 9, _hoisted_3)) : isUrlTerm(propertyTerm) ? (openBlock(), createElementBlock("a", {
                key: 1,
                class: "thn--tool-terms__label thn--tool-terms__label--url",
                href: getTermLink(propertyTerm),
                target: "terms-inherit",
                "aria-label": getTermLabel(propertyTerm)
              }, [
                createElementVNode("span", null, toDisplayString(getTermLabel(propertyTerm)), 1),
                createVNode(unref(icon), {
                  class: "thn--tool-terms__label--icon",
                  "aria-hidden": "true"
                })
              ], 8, _hoisted_4)) : isPopupTerm(propertyTerm) ? (openBlock(), createElementBlock("button", {
                key: 2,
                class: "thn--tool-terms__label thn--tool-terms__label--popup",
                onClick: ($event) => openTerms(propertyTerm),
                type: "button",
                "aria-label": getTermLabel(propertyTerm) || "term"
              }, toDisplayString(getTermLabel(propertyTerm)), 9, _hoisted_5)) : isDefaultPopupTerm(propertyTerm) ? (openBlock(), createElementBlock("button", {
                key: 3,
                class: "thn--tool-terms__label thn--tool-terms__label--popup",
                onClick: _cache[0] || (_cache[0] = ($event) => openDefaultTerms()),
                type: "button",
                "aria-label": defaultTermsLabel.value
              }, toDisplayString(defaultTermsLabel.value), 9, _hoisted_6)) : createCommentVNode("", true),
              !_ctx.termsRequired ? (openBlock(), createElementBlock("p", _hoisted_7, toDisplayString(unref(optionalLabel)), 1)) : createCommentVNode("", true)
            ]);
          }), 128))
        ])) : createCommentVNode("", true),
        (openBlock(true), createElementBlock(Fragment, null, renderList(filteredCustomTerms.value, ({
          termId,
          isRequired,
          term
        }, index) => {
          return openBlock(), createElementBlock("div", {
            key: index,
            class: "thn--tool-terms__item"
          }, [
            createVNode(_sfc_main$1, {
              ref_for: true,
              ref: (ref2) => _ctx.termsInherited && _ctx.propertyTerms?.length ? termRefs.value[index + _ctx.propertyTerms.length] = ref2 : termRefs.value[index] = ref2,
              "error-message": errorMessage.value,
              class: "thn--tool-terms__checkbox",
              disabled: !!_ctx.disabled,
              required: isRequired,
              name: `terms-${index}`,
              id: termId
            }, null, 8, ["error-message", "disabled", "required", "name", "id"]),
            isNotLinkedTerm(term) ? (openBlock(), createElementBlock("label", {
              key: 0,
              class: "thn--tool-terms__label",
              for: termId
            }, toDisplayString(getTermLabel(term)), 9, _hoisted_8)) : isCustomUrlTerm(term) ? (openBlock(), createElementBlock("label", {
              key: 1,
              class: "thn--tool-terms__label thn--tool-terms__label--custom",
              innerHTML: term.content.htmlContent,
              for: termId
            }, null, 8, _hoisted_9)) : isUrlTerm(term) ? (openBlock(), createElementBlock("a", {
              key: 2,
              class: "thn--tool-terms__label thn--tool-terms__label--url",
              href: getTermLink(term),
              target: `terms-${index}`,
              "aria-label": getTermLabel(term)
            }, [
              createElementVNode("span", null, toDisplayString(getTermLabel(term)), 1),
              createVNode(unref(icon), {
                class: "thn--tool-terms__label--icon",
                "aria-hidden": "true"
              })
            ], 8, _hoisted_10)) : isPopupTerm(term) ? (openBlock(), createElementBlock("button", {
              key: 3,
              class: "thn--tool-terms__label thn--tool-terms__label--popup",
              onClick: ($event) => openTerms(term),
              type: "button",
              "aria-label": getTermLabel(term)
            }, toDisplayString(getTermLabel(term)), 9, _hoisted_11)) : isDefaultPopupTerm(term) ? (openBlock(), createElementBlock("button", {
              key: 4,
              class: "thn--tool-terms__label thn--tool-terms__label--popup",
              onClick: _cache[1] || (_cache[1] = ($event) => openDefaultTerms()),
              type: "button",
              "aria-label": defaultTermsLabel.value
            }, toDisplayString(defaultTermsLabel.value), 9, _hoisted_12)) : createCommentVNode("", true),
            !isRequired ? (openBlock(), createElementBlock("p", _hoisted_13, toDisplayString(unref(optionalLabel)), 1)) : createCommentVNode("", true)
          ]);
        }), 128))
      ]);
    };
  }
});

export { _sfc_main as _ };
//# sourceMappingURL=Terms.vue_vue_type_style_index_0_lang.C4aVGK0r.js.map
